// One tee time. Shows both head-to-head matches, each player's tee box,
// their 9-hole course handicap and the resulting strokes given.

import React from 'react';
import { AlertTriangle, Flag } from 'lucide-react';
import DivisionBadge from './DivisionBadge';
import { formatTime, formatDate, pairsFromPairings } from '../utils/helpers';
import { formatHandicap, formatCourseHandicap } from '../utils/formatting';
import { strokesForMatch, resolveTeeSet, MAX_HANDICAP_INDEX, MATCH_HANDICAP_ALLOWANCE } from '../utils/handicap';
import { RESULT_LABELS } from '../utils/constants';

const RESULT_STYLES = {
  win:     'bg-emerald-100 text-emerald-800 border-emerald-200',
  loss:    'bg-red-50 text-red-700 border-red-200',
  tie:     'bg-sky-100 text-sky-800 border-sky-200',
  forfeit: 'bg-orange-100 text-orange-800 border-orange-200',
  pending: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function MatchCard({
  match, golfers, teeSets, courses, isAdmin,
  onChangeResult, onChangeTee, showDate = false, headerExtra,
}) {
  const gmap = new Map((golfers || []).map(g => [g.id, g]));
  const courseTees = (teeSets || []).filter(t => t.course_id === match.course_id);
  const course = (courses || []).find(c => c.id === match.course_id);
  const pairs = pairsFromPairings(match.pairings);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-semibold text-gray-900">{formatTime(match.tee_time)}</span>
          {showDate && <span className="text-gray-500">{formatDate(match.match_date)}</span>}
          <span className="text-gray-600">{course?.name || match.course_id}</span>
          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700">
            {match.nine === 'B9' ? 'Back 9' : 'Front 9'}
          </span>
          {match.round_no && <span className="text-xs text-gray-500">Round {match.round_no}</span>}
        </div>
        {headerExtra}
      </div>

      <div className="divide-y divide-gray-100">
        {pairs.map(([aId, bId], i) => {
          const ga = gmap.get(aId);
          const gb = gmap.get(bId);

          if (!aId || !bId) {
            return (
              <div key={i} className="px-3 py-3 text-sm text-gray-400">
                {ga?.name || 'TBD'} <span className="mx-2">vs</span> {gb?.name || 'TBD'}
              </div>
            );
          }
          if (!ga || !gb) {
            return (
              <div key={i} className="flex items-center gap-2 px-3 py-3 text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                A player in this match is no longer on the roster.
              </div>
            );
          }

          const teeA = resolveTeeSet(match, aId, ga, teeSets);
          const teeB = resolveTeeSet(match, bId, gb, teeSets);
          const s = strokesForMatch({ golfer: ga, teeSet: teeA }, { golfer: gb, teeSet: teeB }, match.nine);

          const row = (g, tee, gets, ch, ph, capped, otherName) => {
            const result = match.results?.[g.id] || 'pending';
            return (
              <div className="flex flex-wrap items-center gap-2 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">{g.name}</span>
                    <DivisionBadge division={g.division} />
                    <span className="text-xs text-gray-500">
                      Idx {formatHandicap(g.handicap_index)}
                      {capped && (
                        <span
                          className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-800"
                          title={`Index capped at ${MAX_HANDICAP_INDEX} for stroke purposes`}
                        >
                          cap {MAX_HANDICAP_INDEX}
                        </span>
                      )}
                      {ch !== null && <> &middot; CH {formatCourseHandicap(ch)}</>}
                      {ph !== null && ph !== undefined && (
                        <span title={`${Math.round(MATCH_HANDICAP_ALLOWANCE * 100)}% allowance`}>
                          {' '}&middot; Plays {formatCourseHandicap(ph)}
                        </span>
                      )}
                    </span>
                    {gets > 0 && (
                      <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-800">
                        +{gets} stroke{gets === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>

                <select
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs disabled:opacity-60"
                  disabled={!isAdmin || !onChangeTee}
                  value={match.golfer_selections?.[g.id]?.tee_set_id || tee?.id || ''}
                  onChange={(e) => onChangeTee?.(match, g.id, e.target.value)}
                  title="Tee box"
                >
                  {courseTees.length === 0 && <option value="">no tees</option>}
                  {courseTees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>

                {onChangeResult ? (
                  <select
                    className={`h-8 rounded-md border px-2 text-xs font-medium disabled:opacity-60 ${RESULT_STYLES[result]}`}
                    disabled={!isAdmin}
                    value={result}
                    onChange={(e) => onChangeResult(match, g.id, otherName, e.target.value)}
                  >
                    {['pending', 'tie', 'win', 'loss', 'forfeit'].map(r => (
                      <option key={r} value={r}>{RESULT_LABELS[r]}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`rounded border px-2 py-1 text-xs font-medium ${RESULT_STYLES[result]}`}>
                    {RESULT_LABELS[result]}
                  </span>
                )}
              </div>
            );
          };

          return (
            <div key={i} className="px-3 py-2">
              {row(ga, teeA, s.aGets, s.aCourseHandicap, s.aPlayingHandicap, s.aIndexCapped, gb.id)}
              <div className="flex items-center gap-2 py-0.5">
                <div className="h-px flex-1 bg-gray-100" />
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500">
                  {s.crossTee && <Flag className="h-3 w-3 text-indigo-500" title="Cross-tee match" />}
                  {s.note}
                </span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>
              {row(gb, teeB, s.bGets, s.bCourseHandicap, s.bPlayingHandicap, s.bIndexCapped, ga.id)}

              {s.notice && (
                <div className="mt-1 rounded bg-gray-50 px-2 py-1 text-xs text-gray-600">
                  {s.notice}
                </div>
              )}

              {s.warning && (
                <div className="mt-1 flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{s.warning}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
